import { util as fabricUtil } from 'fabric';
import { isUserGroup } from './groupUtils';
import { PlantCircle } from '../objects/PlantCircle';
import { textureManager } from '../../../lib/textures/textureManager';
import { supportsTexture } from '../../../lib/textures/materialMappings';
import { restoreFencePostRendering } from '../tools/LineTool';
import { updateOverlapStates } from './overlapDetection';
import { isLinkableParent, ensureObjectId, syncAllLinkedGates } from '../tools/gateLinkUtils';
import { restoreOpeningSymbolRendering } from '../tools/gateSymbolRenderer';
import { restoreTerraceRoofSymbolRendering } from '../tools/terraceRoofSymbolRenderer';
import { isPersistableObject } from './persistableObjects';

// ---------------------------------------------------------------------------
// Module-level load helpers (no React dependencies)
// ---------------------------------------------------------------------------

/**
 * Migration helper for fabric v6→v7 origin-default change (#425).
 *
 * v6's toObject() omitted originX/Y when they matched the default 'left'/'top'.
 * v7's default is 'center'/'center' — so canvases saved under v6 (which
 * include the vast majority of gardens in production) would load with every
 * non-PlantCircle object shifted. Pre-process the serialized JSON to inject
 * v6-compatible defaults; explicit values already in the JSON win (spread
 * order). Recurses into Group children via the `objects` array.
 */
export function injectOriginDefaults(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  const result = { originX: 'left', originY: 'top', ...obj };
  if (Array.isArray(obj.objects)) {
    result.objects = obj.objects.map(injectOriginDefaults);
  }
  return result;
}

/**
 * Migration for the terrace roofing property (#693).
 *
 * The former `ueberdacht` boolean was folgenlos (nirgends gelesen). It becomes
 * the `ueberdachung` select. A saved `true` is the choice the user obviously
 * meant → 'terrassendach'; `false`/missing → 'keine'. The stale boolean is
 * removed so it can't drift. Only runs when `ueberdachung` isn't already set
 * (idempotent, never overwrites a real choice).
 *
 * Mutates the fabric object's `data` in place; returns whether it changed.
 */
export function migrateTerraceRoofing(obj) {
  const data = obj?.data;
  if (!data) return false;
  const objType = data.objectType || data.gardenObjectType;
  if (objType !== 'terrasse') return false;
  if (!('ueberdacht' in data)) return false;

  if (data.ueberdachung === undefined) {
    data.ueberdachung = data.ueberdacht ? 'terrassendach' : 'keine';
  }
  delete data.ueberdacht;
  return true;
}

/**
 * Post-processes a loaded group to replace PlantCircle-typed sub-objects
 * (which enlivenObjects reconstructs as plain fabric.Group) with proper
 * PlantCircle instances. Also handles nested groups recursively.
 */
export function reconstructGroupPlants(group, savedGroupData) {
  const savedSubObjects = savedGroupData.objects || [];
  const currentSubObjects = [...group.getObjects()];

  let hasReplacements = false;
  const newSubObjects = currentSubObjects.map((current, i) => {
    const saved = savedSubObjects[i];
    if (saved?.objectType === 'plant' && saved?.plantProperties) {
      hasReplacements = true;
      return PlantCircle.fromObject(saved);
    }
    if (saved?.objectType === 'group' && current?.type === 'group' && current?.objectType !== 'plant') {
      reconstructGroupPlants(current, saved);
    }
    return current;
  });

  if (hasReplacements) {
    group.remove(...currentSubObjects);
    group.add(...newSubObjects);
  }
}

// ---------------------------------------------------------------------------
// applyCanvasObjects — single source of truth for canvas-load sequence
// ---------------------------------------------------------------------------

/**
 * Applies a serialized canvas JSON onto a live Fabric.js canvas.
 *
 * Both load paths in GardenCanvas — `loadCanvas` (localStorage) and the
 * `externalCanvasData` effect (backend) — call this helper so the load
 * sequence stays in one place. Cross-cutting changes (fabric migrations,
 * new object types, layer-system updates) only need to be made here.
 *
 * Sequence:
 *   1. Clear all non-grid, non-snap-guide objects
 *   2. Split JSON objects into plants and regulars
 *   3. enlivenObjects for regulars (with v6-compat origin defaults from #425)
 *   4. Restore custom props (data, objectType, renderOptions, perPixelTargetFind, padding)
 *   5. Reconstruct PlantCircle sub-objects inside Groups
 *   6. PlantCircle.fromObject for top-level plants
 *   7. assignLayerToObject + enforceLayerOrder
 *   8. updateOverlapStates (if plants present)
 *   9. Re-apply textures + fence-post rendering
 *  10. applyTabObjectBehavior
 *
 * The helper restores `canvas.renderOnAddRemove = true` on the error path
 * before re-throwing, so callers can rely on the canvas being in a sane
 * rendering state after a failed load.
 *
 * @param {fabric.Canvas} canvas - Live Fabric.js canvas
 * @param {Object|null} json - Serialized canvas JSON (`{ version, objects }`)
 * @param {Object} options
 * @param {'localStorage'|'backend'} options.source - For error-log differentiation
 * @param {(obj: fabric.Object, canvas?: fabric.Canvas) => void} options.assignLayerToObject
 * @param {(canvas: fabric.Canvas) => void} options.enforceLayerOrder
 * @param {(canvas: fabric.Canvas, tab: string, readOnly: boolean) => void} options.applyTabObjectBehavior
 * @param {string} options.activeTab - Current editor tab (zeichnen|pflanzen|garten)
 * @param {boolean} options.readOnly - Whether the canvas is in read-only mode
 * @returns {Promise<void>}
 */
export async function applyCanvasObjects(canvas, json, {
  source,
  assignLayerToObject,
  enforceLayerOrder,
  applyTabObjectBehavior,
  activeTab,
  readOnly,
}) {
  if (!canvas) return;

  // Guard against interleaved concurrent loads (#622): the clear below runs
  // synchronously, but the adds happen after an await — two overlapping calls
  // would each re-add the full object set (clear A, clear B, add A, add B),
  // duplicating every object, which the auto-save then persists. Each call
  // claims a monotonically increasing token on the canvas; after the await,
  // a call whose token is no longer current aborts instead of adding, so the
  // most recently started load always wins.
  const loadToken = (canvas.__applyCanvasObjectsToken ?? 0) + 1;
  canvas.__applyCanvasObjectsToken = loadToken;

  // 1. Clear only non-grid objects
  const allObjects = canvas.getObjects();
  const objectsToRemove = allObjects.filter(
    obj => !obj.data?.isGridLine && !obj.data?.isSnapGuide
  );
  objectsToRemove.forEach(obj => canvas.remove(obj));

  if (!json?.objects || json.objects.length === 0) return;

  // Heal gardens that already carry editor scaffolding: drawing-tool markers
  // and snap indicators leaked by the drawing tools (#682), and vertex handles
  // written by the backend save path while edit mode was active (#683). None of
  // it is user content, and since fabric does not serialize `selectable` /
  // `evented` it all comes back fully clickable. Drop it on load; both save
  // paths keep new ones out, so a garden cleans itself up on the next save.
  const savedObjects = json.objects.filter(isPersistableObject);
  if (savedObjects.length === 0) return;

  canvas.renderOnAddRemove = false;

  try {
    // 2. Split plant / regular
    const plantObjects = [];
    const regularObjects = [];
    savedObjects.forEach(obj => {
      if (obj.objectType === 'plant') {
        plantObjects.push(obj);
      } else {
        regularObjects.push(obj);
      }
    });

    // 3-5. Load regular objects via enlivenObjects
    if (regularObjects.length > 0) {
      const objectsWithOrigin = regularObjects.map(injectOriginDefaults);
      const enlivenedObjects = await fabricUtil.enlivenObjects(objectsWithOrigin);

      // Stale after the await? A newer load has cleared the canvas and owns
      // it now — abort without adding. Don't touch renderOnAddRemove: the
      // newer (current) call manages and restores that flag itself.
      if (canvas.__applyCanvasObjectsToken !== loadToken) return;

      enlivenedObjects.forEach((obj, index) => {
        const savedObj = regularObjects[index];

        // Restore custom properties (Fabric.js doesn't always restore them correctly)
        if (savedObj.data) obj.data = savedObj.data;
        if (savedObj.objectType) obj.objectType = savedObj.objectType;
        if (savedObj.renderOptions) obj.renderOptions = savedObj.renderOptions;

        // Re-apply perPixelTargetFind — Fabric.js does not serialize this property.
        // Only narrow shapes (line, bezier path) need pixel-accurate detection; others use AABB.
        if (obj.type === 'line' || obj.type === 'path') {
          obj.perPixelTargetFind = true;
        }
        if (obj.type === 'line') {
          obj.padding = 8;
        }

        // Post-process groups: replace PlantCircle sub-objects that
        // enlivenObjects reconstructed as plain fabric.Group instances
        if (savedObj.objectType === 'group' && isUserGroup(obj)) {
          reconstructGroupPlants(obj, savedObj);
        }

        assignLayerToObject(obj);
        canvas.add(obj);
      });
    }

    // 6. Load plant objects
    if (plantObjects.length > 0) {
      plantObjects.forEach(savedPlant => {
        try {
          if (!savedPlant.plantProperties) {
            console.warn('Skipping plant with missing plantProperties:', savedPlant);
            return;
          }
          const plantCircle = PlantCircle.fromObject(savedPlant);
          assignLayerToObject(plantCircle);
          canvas.add(plantCircle);
        } catch (error) {
          console.error(`Error loading plant from ${source}:`, savedPlant, error);
        }
      });
    }

    canvas.discardActiveObject();
    canvas.renderOnAddRemove = true;

    // 7. Layer order
    enforceLayerOrder(canvas);

    // 8. Recalculate overlaps
    if (plantObjects.length > 0) {
      updateOverlapStates(canvas);
    }

    // 9. Re-apply textures + fence posts; lazy-migrate stable objectIds for
    //    linkable structures and resync gate links (#611)
    canvas.getObjects().forEach((obj) => {
      const objType = obj.data?.objectType || obj.data?.gardenObjectType;
      if (objType && supportsTexture(objType) && !obj.data?.useCustomColor) {
        textureManager.applyTexture(obj, canvas);
      }
      migrateTerraceRoofing(obj);
      restoreFencePostRendering(obj);
      restoreOpeningSymbolRendering(obj);
      // #693: roofed terraces re-install their plan symbol. Must run AFTER
      // migrateTerraceRoofing so `data.ueberdachung` is already normalized.
      restoreTerraceRoofSymbolRendering(obj);
      if (isLinkableParent(obj)) {
        ensureObjectId(obj);
      }
    });
    syncAllLinkedGates(canvas);

    canvas.renderAll();

    // 10. Tab behavior
    applyTabObjectBehavior(canvas, activeTab, readOnly);
  } catch (error) {
    // Restore rendering state before propagating
    canvas.renderOnAddRemove = true;
    throw error;
  }
}
